import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Study, StudyAttachment } from '../../types';

export default function StudyDetail() {
  const { id } = useParams<{ id: string }>();
  const [study, setStudy] = useState<Study | null>(null);
  const [attachments, setAttachments] = useState<StudyAttachment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStudy = async () => {
      const { data: studyData } = await supabase
        .from('studies')
        .select('*')
        .eq('id', id)
        .single();
      setStudy(studyData);

      const { data: attachData } = await supabase
        .from('study_attachments')
        .select('*')
        .eq('study_id', id);
      setAttachments(attachData || []);
      setLoading(false);
    };
    fetchStudy();
  }, [id]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
      </div>
    );
  }

  if (!study) {
    return (
      <div className="text-center py-8">
        <p className="text-[#8E8E93]">Estudio no encontrado</p>
        <Link to="/studies" className="text-primary mt-2 block">Volver a estudios</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link to="/studies" className="text-primary">← Volver</Link>
      
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-white mb-2">{study.title}</h1>
        <p className="text-sm text-[#8E8E93]">📅 {formatDate(study.study_date)}</p>

        {study.description && (
          <p className="text-[#B3B3B3] mt-4">{study.description}</p>
        )}

        {study.notes && (
          <div className="mt-6">
            <h2 className="font-semibold text-[#E5E5EA] mb-2">Notas</h2>
            <div className="bg-[#2C2C2E] p-4 rounded-lg whitespace-pre-wrap text-sm">
              {study.notes}
            </div>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {study.pdf_url && (
            <a href={study.pdf_url} target="_blank" rel="noopener noreferrer" className="btn-primary block text-center">
              📄 Ver PDF
            </a>
          )}
          {study.video_embed_url && (
            <a href={study.video_embed_url} target="_blank" rel="noopener noreferrer" className="btn-secondary block text-center">
              ▶ Ver Video
            </a>
          )}
        </div>

        {attachments.length > 0 && (
          <div className="mt-6">
            <h2 className="font-semibold text-[#E5E5EA] mb-2">Archivos Adjuntos</h2>
            <div className="space-y-2">
              {attachments.map((att) => (
                <a 
                  key={att.id} 
                  href={att.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block p-3 bg-[#2C2C2E] rounded-lg hover:bg-[#3A3A3C]"
                >
                  📎 {att.filename}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
